import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  CheckCircle,
  Calendar,
  Building,
  AlertCircle,
  X,
  Eye,
  Package,
  AlertTriangle,
  User,
  FileText,
  Download,
  IndianRupee,
  Trash2
} from 'lucide-react';

import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';

import { formatDate, formatCurrency } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import quotationDashboardService from '../../../api/crm/quotation-dashboard/quotationDashboardService';
import quotationService from '../../../api/crm/quotations/quotationService';
import orderService from '../../../api/crm/orders/orderService';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { toast } from 'react-hot-toast';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const ConvertOrderListing = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial filter values from URL (for persistence across refresh)
  const initialTab = searchParams.get('tab') === 'ordered' ? 'ordered' : 'ready';
  const initialSearch = searchParams.get('search') || '';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const isInitialSearchResetMount = useRef(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab); // 'ready' | 'ordered'

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });

  // Selection & Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Ready-to-order "Order Placed" confirmation for quotations
  const [showQuoteOrderPlacedConfirm, setShowQuoteOrderPlacedConfirm] = useState(false);
  const [selectedQuotationForOrder, setSelectedQuotationForOrder] = useState(null);
  const [margOrderId, setMargOrderId] = useState('');

  // Loading state for invoice downloads (track by quoteId)
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  // Single delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk delete state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
      // Skip the page reset on the initial mount so the URL-restored page is kept
      if (isInitialSearchResetMount.current) {
        isInitialSearchResetMount.current = false;
        return;
      }
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync filter/tab/pagination state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['tab', 'search', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'ready') params.set('tab', activeTab);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));

      return params;
    }, { replace: true });
  }, [
    activeTab,
    debouncedSearchTerm,
    dateRange,
    currentPage,
    itemsPerPage,
    setSearchParams,
  ]);

  useEffect(() => {
    setSelectedRows(new Set());
    setShowInlineSelectedFormats(false);
  }, [activeTab]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);
  // React Query: Fetch data for active tab
  const {
    data: listData,
    isLoading: loading,
    isError,
    error: fetchError
  } = useQuery({
    queryKey: ['convertOrderList', activeTab, debouncedSearchTerm, dateRange, currentPage, itemsPerPage, refreshKey],
    queryFn: async () => {
      if (activeTab === 'ready') {
        // Ready to Order tab: fetch quotations with status "order"
        const filters = {
          search: debouncedSearchTerm.trim() || undefined,
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          dateRange: (dateRange.startDate || dateRange.endDate) ? 'custom' : undefined,
          status: 'order'
        };
        const paginationParams = {
          tab: 'all',
          page: currentPage,
          limit: itemsPerPage
        };

        const result = await quotationDashboardService.getQuotationList(filters, paginationParams);

        if (result.success) {
          return {
            rows: result.quotations || [],
            pagination: result.pagination || null
          };
        }
        throw new Error(result.error || 'Failed to fetch quotations');
      }

      // Ordered tab: fetch orders from orders collection
      const filters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: 'confirmed',
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage,
        limit: itemsPerPage
      };

      const result = await orderService.getOrders(filters);

      if (result.success) {
        return {
          rows: result.orders || [],
          pagination: result.pagination || null
        };
      }
      throw new Error(result.error || 'Failed to fetch orders');
    },
    staleTime: 0,
    retry: 2
  });
  const rows = listData?.rows || [];
  const pagination = listData?.pagination || null;

  const baseExportFilters = useMemo(() => {
    if (activeTab === 'ready') {
      return {
        convertOrderTab: 'ready',
        search: debouncedSearchTerm.trim() || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        dateRange: dateRange.startDate || dateRange.endDate ? 'custom' : undefined,
        status: 'order',
      };
    }
    return {
      convertOrderTab: 'ordered',
      search: debouncedSearchTerm.trim() || undefined,
      status: 'confirmed',
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };
  }, [activeTab, debouncedSearchTerm, dateRange]);

  const currentPageIdsForExport = useMemo(
    () => rows.map((r) => String(r._id || r.id || '')).filter(Boolean),
    [rows],
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? overrideCurrentPageIds ?? currentPageIdsForExport
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-convert-order-listing',
            format,
            scope,
            filters: {
              ...baseExportFilters,
              ...(overrideFilters != null ? overrideFilters : {}),
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        },
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `convert-order-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, currentPage, itemsPerPage, currentPageIdsForExport],
  );

  const handleConvertOrderSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = rows
        .filter((r) => selectedRows.has(r._id || r.id))
        .map((r) => String(r._id || r.id))
        .filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(
          `${ids.length} row${ids.length === 1 ? '' : 's'} exported.`,
        );
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, rows],
  );

  const handleConvertOrderScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

      const pageItems = rows;
      const totalFiltered = Number(pagination?.totalRecords ?? 0);

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No records match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              search: baseExportFilters.search,
              status: baseExportFilters.status,
              convertOrderTab: baseExportFilters.convertOrderTab,
              startDate: rangeStart,
              endDate: rangeEnd,
              ...(activeTab === 'ready' ? { dateRange: 'custom' } : {}),
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [activeTab, baseExportFilters, pagination?.totalRecords, rows, requestBackendExport],
  );

  // Helper function to generate order number from quote ID
  // Converts Q-2025-0010 to ORD20250010
  const generateOrderNumberFromQuoteId = (quoteId) => {
    if (!quoteId) return '';
    // Remove Q- prefix and all dashes, then add ORD prefix
    const cleaned = quoteId.toString().replace(/^Q-?/i, '').replace(/-/g, '');
    return `ORD${cleaned}`;
  };

  // Mutation: create order from quotation (Ready to Order tab)
  const createOrderFromQuotationMutation = useMutation({
    mutationFn: async (quotationRow) => {
      const quoteId = quotationRow._id || quotationRow.id;
      if (!quoteId) {
        throw new Error('Invalid quotation ID');
      }

      // Fetch full quotation details to build order payload
      const quoteResult = await quotationService.getQuotationById(quoteId);
      if (!quoteResult.success || !quoteResult.quotation) {
        throw new Error(quoteResult.error || 'Failed to load quotation details');
      }

      const quotation = quoteResult.quotation;
      const margOrderIdInput = quotationRow.margOrderId; // Get MARG ID from input

      // Generate order number from quote ID (keep this as orderNumber, but use MARG ID as orderId)
      const quoteIdForOrder = quotation.quoteNumber || quotation.quoteId || quoteId;
      const generatedOrderNumber = generateOrderNumberFromQuoteId(quoteIdForOrder);

      // Use Lead data instead of Customer/Contact collections
      // Note: customerId is no longer required - data will be fetched from quotation -> lead -> opportunity
      const lead = quotation.lead || {};
      const billingAddressSrc = quotation.billingAddress || {};
      const shippingAddressSrc = quotation.shippingAddress || {};

      // Create customer-like object from Lead data (for order creation compatibility)
      const customer = {
        companyName: lead.company || billingAddressSrc.company || '',
        name: lead.company || billingAddressSrc.company || '',
        email: lead.email || '',
        phone: lead.phone || shippingAddressSrc.phone || '',
        contactPerson: lead.firstName && lead.lastName
          ? `${lead.firstName} ${lead.lastName}`.trim()
          : lead.firstName || lead.lastName || '',
        gstin: billingAddressSrc.gstin || '',
        address: lead.address?.street || '',
        city: lead.address?.city || '',
        state: lead.address?.state || '',
        pincode: lead.address?.pincode || '',
        billingAddress: {
          address: lead.address?.street || '',
          street: lead.address?.street || '',
          city: lead.address?.city || '',
          state: lead.address?.state || '',
          pincode: lead.address?.pincode || ''
        },
        shippingAddress: {
          address: lead.address?.street || '',
          street: lead.address?.street || '',
          city: lead.address?.city || '',
          state: lead.address?.state || '',
          pincode: lead.address?.pincode || ''
        }
      };

      // Create contact-like object from Lead data
      const contact = {
        email: lead.email || '',
        phone: lead.phone || '',
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        designation: lead.designation || ''
      };

      // Helper function to validate and convert date
      const parseDate = (dateValue) => {
        if (!dateValue) return null;
        // If it's already a Date object, check if valid
        if (dateValue instanceof Date) {
          return isNaN(dateValue.getTime()) ? null : dateValue;
        }
        // If it's a string, try to parse it
        if (typeof dateValue === 'string') {
          // If it's an empty string or just whitespace, return null
          if (!dateValue.trim()) return null;
          const parsed = new Date(dateValue);
          return isNaN(parsed.getTime()) ? null : parsed.toISOString();
        }
        // Try to convert to Date
        try {
          const parsed = new Date(dateValue);
          return isNaN(parsed.getTime()) ? null : parsed.toISOString();
        } catch {
          return null;
        }
      };

      // Map items from quotation to order items
      const items = Array.isArray(quotation.items)
        ? quotation.items.map((item) => ({
          productId: item.productId || null,
          productName: item.productName,
          sku: item.sku || '',
          description: item.description || '',
          quantity: item.quantity,
          mrp: item.mrp || 0,
          unitPrice: item.unitPrice,
          unitPricePTS: item.unitPricePTS || 0,
          batch: item.batch || '',
          expiry: parseDate(item.expiry),
          manufacturing: parseDate(item.manufacturing),
          discount: item.discount || 0,
          tax: item.tax || 0,
          lineTotal: item.lineTotal || 0
        }))
        : [];

      // Helper function to extract name from city/state/country object or string
      const extractName = (field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        return field.name || field.id || '';
      };

      if (!items.length) {
        throw new Error('Quotation has no items. Cannot create order.');
      }

      const orderData = {
        quotationId: quotation._id,
        quoteId: quotation.quoteNumber || quotation.quoteId || null, // Quote number (e.g., "Q-2025-0010")
        orderId: margOrderIdInput, // Use the user-inputted MARG ID as the orderId
        orderNumber: margOrderIdInput, // Use MARG ID as orderNumber as well
        email: contact.email || customer.email || '',
        phone: contact.phone || customer.phone || '',
        billingAddress: {
          company:
            billingAddressSrc.company ||
            customer.companyName ||
            customer.name ||
            'N/A',
          address:
            billingAddressSrc.address ||
            billingAddressSrc.street ||
            customer.billingAddress?.address ||
            customer.billingAddress?.street ||
            customer.address ||
            '',
          city:
            extractName(billingAddressSrc.city) ||
            extractName(customer.billingAddress?.city) ||
            extractName(customer.city) ||
            '',
          state:
            extractName(billingAddressSrc.state) ||
            extractName(customer.billingAddress?.state) ||
            extractName(customer.state) ||
            '',
          country:
            extractName(billingAddressSrc.country) ||
            extractName(customer.billingAddress?.country) ||
            extractName(customer.country) ||
            '',
          pincode:
            billingAddressSrc.pincode ||
            customer.billingAddress?.pincode ||
            customer.pincode ||
            '',
          gstin: billingAddressSrc.gstin || customer.gstin || ''
        },
        shippingAddress: {
          company:
            shippingAddressSrc.company ||
            billingAddressSrc.company ||
            customer.companyName ||
            customer.name ||
            'N/A',
          address:
            shippingAddressSrc.address ||
            shippingAddressSrc.street ||
            billingAddressSrc.address ||
            billingAddressSrc.street ||
            customer.shippingAddress?.address ||
            customer.shippingAddress?.street ||
            customer.billingAddress?.address ||
            customer.billingAddress?.street ||
            customer.address ||
            '',
          landmark: shippingAddressSrc.landmark || '',
          city:
            extractName(shippingAddressSrc.city) ||
            extractName(billingAddressSrc.city) ||
            extractName(customer.shippingAddress?.city) ||
            extractName(customer.billingAddress?.city) ||
            extractName(customer.city) ||
            '',
          state:
            extractName(shippingAddressSrc.state) ||
            extractName(billingAddressSrc.state) ||
            extractName(customer.shippingAddress?.state) ||
            extractName(customer.billingAddress?.state) ||
            extractName(customer.state) ||
            '',
          country:
            extractName(shippingAddressSrc.country) ||
            extractName(billingAddressSrc.country) ||
            extractName(customer.shippingAddress?.country) ||
            extractName(customer.billingAddress?.country) ||
            extractName(customer.country) ||
            '',
          pincode:
            shippingAddressSrc.pincode ||
            billingAddressSrc.pincode ||
            customer.shippingAddress?.pincode ||
            customer.billingAddress?.pincode ||
            customer.pincode ||
            '',
          contact: shippingAddressSrc.contact || '',
          phone: shippingAddressSrc.phone || contact.phone || customer.phone || '',
          deliveryInstructions: shippingAddressSrc.deliveryInstructions || quotation.notes || ''
        },
        items,
        subtotal: quotation.subtotal || 0,
        totalDiscount: quotation.discountAmount || 0,
        totalTax: quotation.taxAmount || 0,
        grandTotal: quotation.totalAmount || 0,
        status: 'confirmed',
        deliveryType: 'Standard (5-7 days)',
        notes: quotation.notes || ''
      };

      const createResult = await orderService.createOrder(orderData);
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create order');
      }

      return createResult;
    },
    onSuccess: () => {
      // Refresh list and close modal
      queryClient.invalidateQueries({
        queryKey: ['convertOrderList', activeTab, debouncedSearchTerm, dateRange, currentPage, itemsPerPage]
      });
      setShowQuoteOrderPlacedConfirm(false);
      setSelectedQuotationForOrder(null);
      setMargOrderId(''); // Reset input
      alert('Order placed successfully');
    },
    onError: (error) => {
      alert(error.message || 'Failed to create order from quotation');
    }
  });

  const handleView = (quotation) => {
    const quoteId = quotation._id || quotation.id || quotation.quoteId;
    if (!quoteId) {
      return;
    }

    // Navigate to send-track page
    navigate(`/crm/quotation-proposal/send-track/${quoteId}?page=order`);
  };

  const handleViewOrder = (order) => {
    // Use quotationId from API response, fallback to quoteId (quote number)
    const id = order.quotationId || order.quoteId;
    if (!id) {
      return;
    }

    // Navigate to send-track page with the ID
    navigate(`/crm/quotation-proposal/send-track/${id}?page=ordered`);
  };

  const handleDownloadInvoice = async (row) => {
    // For "ready" tab, use quotation ID directly
    // For "ordered" tab, use quotationId from order
    const quoteId = activeTab === 'ready'
      ? (row._id || row.id || row.quoteId)
      : (row.quotationId || row.quoteId);

    if (!quoteId) {
      alert('Invalid quotation ID. Cannot download invoice.');
      return;
    }

    try {
      setDownloadingInvoiceId(quoteId);
      const result = await quotationService.downloadInvoice(quoteId);

      if (!result.success) {
        alert(result.error || 'Failed to download invoice PDF. Please try again.');
      }
    } catch (error) {
      alert('Failed to download invoice PDF. Please try again.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleQuoteOrderPlaced = (quotation) => {
    setMargOrderId(''); // Reset input when opening modal
    setSelectedQuotationForOrder(quotation);
    setShowQuoteOrderPlacedConfirm(true);
  };

  const handleConfirmQuoteOrderPlaced = () => {
    if (!selectedQuotationForOrder) return;
    createOrderFromQuotationMutation.mutate({ ...selectedQuotationForOrder, margOrderId });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1);
  };

  // Switch tab and reset all filters (search, date range, page) to defaults
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSearchQuery('');
    setDebouncedSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1);
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    // items is the current page data
    const allSelected = items.every(item => selectedRows.has(item._id || item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item._id || item.id));
      } else {
        items.forEach(item => newSelection.add(item._id || item.id));
      }
      return newSelection;
    });
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.success("List refreshed");
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // ── Delete handlers ────────────────────────────────────────────────────────
  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRowToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    setIsDeleting(true);
    try {
      let result;
      if (activeTab === 'ready') {
        // Delete quotation
        const id = rowToDelete._id || rowToDelete.id;
        result = await quotationService.deleteQuotation(id);
      } else {
        // Delete order
        const id = rowToDelete._id || rowToDelete.id;
        result = await orderService.deleteOrder(id);
      }

      if (result.success) {
        queryClient.invalidateQueries(['convertOrderList']);
        toast.success(`${activeTab === 'ready' ? 'Quotation' : 'Order'} deleted successfully`);
        setShowDeleteModal(false);
        setRowToDelete(null);
      } else {
        toast.error(result.error || 'Failed to delete');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedRows.size > 0) setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setIsBulkDeleting(true);
    const ids = Array.from(selectedRows);
    try {
      const results = await Promise.allSettled(
        ids.map(id =>
          activeTab === 'ready'
            ? quotationService.deleteQuotation(id)
            : orderService.deleteOrder(id)
        )
      );
      const succeeded = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
      const failed = ids.length - succeeded;

      queryClient.invalidateQueries(['convertOrderList']);
      setSelectedRows(new Set());
      setShowBulkDeleteModal(false);

      if (failed === 0) {
        toast.success(`${succeeded} ${activeTab === 'ready' ? 'quotation' : 'order'}${succeeded > 1 ? 's' : ''} deleted successfully!`);
      } else {
        toast.error(`${succeeded} deleted, ${failed} failed. Please try again.`);
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Skeleton loading component
  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
      <td className="px-3 py-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
      <td className="px-3 py-4"><div className="h-8 bg-gray-200 rounded w-20"></div></td>
    </tr>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Convert to Order</h1>
          <p className="text-gray-600">
            Manage quotations that are ready to convert to orders and view already created orders.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedRows.size > 0 && (
            <PermissionGate permission="quotation_management" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteClick}
                loading={isBulkDeleting}
                className="h-10"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="quotation_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading || ((!rows || rows.length === 0) && selectedRows.size === 0)}
                className={`w-full sm:w-auto h-10${
                  selectedRows.size > 0 ? ' bg-blue-50 border-blue-200 text-blue-600' : ''
                }`}
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleConvertOrderSelectedFormatExport}
              />
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 pt-4">
          <div className="flex space-x-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => handleTabChange('ready')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'ready'
                ? 'border-agrosaf-green text-agrosaf-green'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              Ready to place order
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('ordered')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'ordered'
                ? 'border-agrosaf-green text-agrosaf-green'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              Placed Orders
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-row flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Order ID, Quote ID, Company Name, Customer Name..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
            />
          </div>

          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(newRange) => {
              setDateRange(newRange);
              setCurrentPage(1);
            }}
            allowFutureDates={true}
          />

          {(searchQuery || (dateRange.startDate || dateRange.endDate)) && (
            <Button
              variant="outline"
              icon={<X className="h-4 w-4" />}
              onClick={handleClearFilters}
              className="flex-shrink-0 whitespace-nowrap"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      {/* Error Message */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-800">{fetchError?.message || 'Failed to load quotations'}</p>
        </div>
      )}
      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer"
                      checked={rows.length > 0 && rows.every(row => selectedRows.has(row._id || row.id))}
                      onChange={() => handleSelectAll(rows)}
                    />
                  </th>
                  {activeTab !== 'ready' && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <Package className="h-4 w-4 mr-2" />
                        Order ID
                      </div>
                    </th>
                  )}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Quote ID
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      Company Name
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Customer Name
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <IndianRupee className="h-4 w-4 mr-2 font-bold" />
                      Quote Value
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {activeTab === 'ready' ? 'Order At' : 'Ordered At'}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Status
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <>
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'ready' ? 8 : 9} className="px-3 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <CheckCircle className="h-12 w-12 text-gray-400 mb-3" />
                        <p className="text-sm text-gray-500 font-medium">No relevant order found</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {searchQuery || dateRange.startDate || dateRange.endDate
                            ? 'Try adjusting your search or filters'
                            : activeTab === 'ready'
                              ? 'Quotations with status "Order" will appear here'
                              : 'Orders will appear here once created'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    // Map fields based on active tab
                    let companyName = 'N/A';
                    let contactName = 'N/A';
                    let amount = 0;
                    let identifier = 'N/A';
                    let orderIdValue = 'N/A';
                    let statusLabel = '';
                    let dateValue = null;

                    if (activeTab === 'ready') {
                      companyName = row.companyName || 'N/A';
                      contactName = row.customerName || 'N/A';
                      amount = row.totalAmount || row.value || 0;
                      identifier = row.quoteId || row.quoteNumber || row._id || 'N/A';
                      orderIdValue = row.orderId || 'N/A';
                      statusLabel = row.status?.toLowerCase() === 'order' ? 'Ready to Order' : 'Order';
                      dateValue = row.updatedAt || null; // Order At - use updatedAt
                    } else {
                      // Use new API response format: orderId, quoteId, companyName, customerName, quoteValue, orderedAt, status
                      companyName = row.companyName || 'N/A';
                      contactName = row.customerName || 'N/A';
                      amount = row.quoteValue || 0;
                      identifier = row.quoteId || 'N/A';
                      orderIdValue = row.orderId || 'N/A';
                      statusLabel = 'Ordered';
                      dateValue = row.orderedAt || null; // Ordered At - use orderedAt from API
                    }

                    return (
                      <tr key={row._id || row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer"
                            checked={selectedRows.has(row._id || row.id)}
                            onChange={() => handleSelectRow(row._id || row.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        {activeTab !== 'ready' && (
                          <td className="px-3 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{orderIdValue}</span>
                          </td>
                        )}
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{identifier}</span>
                        </td>
                        <td className="px-3 py-4">
                          <div className="max-w-[160px] truncate" title={companyName}>
                            <span className="text-sm text-gray-900 capitalize">{companyName}</span>
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="max-w-[160px] truncate" title={contactName}>
                            <span className="text-sm text-gray-900 capitalize">{contactName}</span>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">₹{formatIndianAmount(amount)}</span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{formatDate(dateValue, { includeTime: true })}</span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                          {activeTab === 'ready' ? (
                            <div className="flex items-center space-x-3">
                              <PermissionGate permission="quotation_management" action="read">
                                <button
                                  type="button"
                                  onClick={() => handleView(row)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="quotation_management" action="read">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadInvoice(row)}
                                  className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={downloadingInvoiceId === (row._id || row.id || row.quoteId)}
                                  title={downloadingInvoiceId === (row._id || row.id || row.quoteId) ? 'Downloading...' : 'Download Invoice'}
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="quotation_management" action="create">
                                <button
                                  type="button"
                                  onClick={() => handleQuoteOrderPlaced(row)}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={createOrderFromQuotationMutation.isPending}
                                  title="Order Placed"
                                >
                                  <Package className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="quotation_management" action="delete">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(row)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3">
                              <PermissionGate permission="quotation_management" action="read">
                                <button
                                  type="button"
                                  onClick={() => handleViewOrder(row)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="quotation_management" action="read">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadInvoice(row)}
                                  className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={downloadingInvoiceId === (row.quotationId || row.quoteId)}
                                  title={downloadingInvoiceId === (row.quotationId || row.quoteId) ? 'Downloading...' : 'Download Invoice'}
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="quotation_management" action="delete">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(row)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(() => {
            const totalItems = Number(pagination?.totalRecords || rows.length || 0);
            const totalPages = Number(pagination?.totalPages || 1);
            const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
            const currentPageNum = pagination?.page || currentPage || 1;
            // Show pagination only if total items exceed the limit (meaning there are multiple pages)
            const shouldShowPagination = !loading && totalItems > effectivePaginationLimit;

            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing {((currentPageNum - 1) * effectivePaginationLimit + 1)} to {Math.min(currentPageNum * effectivePaginationLimit, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Items per page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPageNum - 1)}
                      disabled={currentPageNum <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const pages = [];

                        if (totalPages <= 10) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (currentPageNum <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (currentPageNum >= totalPages - 3) {
                            pages.push('ellipsis');
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push('ellipsis');
                            for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                ? 'bg-agrosaf-green text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPageNum + 1)}
                      disabled={currentPageNum >= totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Quote Order Placed Confirmation Modal (Ready to Order tab) */}
      {
        showQuoteOrderPlacedConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Order Placed</h3>
                <button
                  onClick={() => {
                    setShowQuoteOrderPlacedConfirm(false);
                    setSelectedQuotationForOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={createOrderFromQuotationMutation.isPending}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-full">
                    {/* MARG Order Id Input */}
                    <div className="mb-4 mt-3">
                      <label htmlFor="margOrderId" className="block text-sm font-medium text-gray-700 mb-1">
                        Enter Marg Order Number (APPL012345) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="margOrderId"
                        value={margOrderId}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 20 && /^[a-zA-Z0-9\-_]*$/.test(val)) {
                            setMargOrderId(val);
                          }
                        }}
                        placeholder="e.g., APPL012345"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        required
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`text-xs ${margOrderId.length >= 20 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                          {margOrderId.length}/20
                        </span>
                      </div>
                    </div>

                    {selectedQuotationForOrder && (() => {
                      const quoteIdForOrder = selectedQuotationForOrder.quoteId || selectedQuotationForOrder.quoteNumber || selectedQuotationForOrder._id || '';
                      return (
                        <div className="text-sm text-gray-600 space-y-1 bg-gray-50 p-3 rounded-md">
                          <p>
                            <strong>Quote ID:</strong>{' '}
                            {quoteIdForOrder || 'N/A'}
                          </p>
                          <p className="capitalize break-all break-word">
                            <strong>Company:</strong> {selectedQuotationForOrder.companyName || 'N/A'}
                          </p>
                          <p>
                            <strong>Quote Value:</strong> ₹{formatIndianAmount(selectedQuotationForOrder.totalAmount || selectedQuotationForOrder.value || 0)}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowQuoteOrderPlacedConfirm(false);
                    setSelectedQuotationForOrder(null);
                    setMargOrderId(''); // Reset input
                  }}
                  disabled={createOrderFromQuotationMutation.isPending}
                >
                  Cancel
                </Button>
                <PermissionGate permission="order_management" action="create">
                  <Button
                    variant="primary"
                    onClick={handleConfirmQuoteOrderPlaced}
                    disabled={createOrderFromQuotationMutation.isPending || !margOrderId.trim()}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    icon={<Package className="h-4 w-4" />}
                  >
                    {createOrderFromQuotationMutation.isPending ? 'Creating...' : 'Confirm Order Placed'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }
      {/* Single Delete Confirmation Modal */}
      {showDeleteModal && rowToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this {activeTab === 'ready' ? 'quotation' : 'order'}? This action cannot be undone.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-6 text-sm text-gray-700 space-y-1">
                {activeTab === 'ready' ? (
                  <>
                    <p><span className="font-medium">Quote ID:</span> {rowToDelete.quoteId || rowToDelete.quoteNumber || 'N/A'}</p>
                    <p className="capitalize"><span className="font-medium">Company:</span> {rowToDelete.companyName || 'N/A'}</p>
                  </>
                ) : (
                  <>
                    <p><span className="font-medium">Order ID:</span> {rowToDelete.orderId || 'N/A'}</p>
                    <p><span className="font-medium">Quote ID:</span> {rowToDelete.quoteId || 'N/A'}</p>
                    <p className="capitalize"><span className="font-medium">Company:</span> {rowToDelete.companyName || 'N/A'}</p>
                  </>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={handleDeleteCancel} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  icon={
                    isDeleting
                      ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      : <Trash2 className="h-4 w-4" />
                  }
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Bulk Deletion</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-bold">{selectedRows.size}</span> selected{' '}
                {activeTab === 'ready' ? 'quotation' : 'order'}{selectedRows.size > 1 ? 's' : ''}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} disabled={isBulkDeleting}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                  icon={
                    isBulkDeleting
                      ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      : <Trash2 className="h-4 w-4" />
                  }
                >
                  {isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={
          activeTab === 'ready'
            ? 'Export Ready to place order'
            : 'Export Placed Orders'
        }
        showDateRange
        dateRangeAllowFutureEnd
        scopedFormatExport={{
          onExport: handleConvertOrderScopedExport,
        }}
      />
    </div >
  );
};

export default ConvertOrderListing;

